import React, { createContext, useContext, useEffect, useState } from 'react';
import { FlagResponse } from '@getbread/feature-flag-sdk';
import { getFeatureFlags } from '../lib/handlers/get-feature-flags';
import { logger } from '../lib/logger';
import { FCWithChildren } from '../lib/types';
import { setAdditionalContext } from '../lib/analytics';
import { useAppConfig } from './AppConfigContext';
import { useDeviceID, useExperienceKeys } from './XPropsContext';
import { Loader } from './LoadingManager';

export interface FeatureFlags {
  enableSplitPayAuthEnhancement: boolean;
  disableColoradoAPR: boolean;
  enableWebOTPAutofill: boolean;
  isExchangedStreamlined: boolean;
  enableAddressLogging: boolean;
  useSmartyStreetsAPI: boolean;
  useVGSPaymentProvider: boolean;
  onCheckoutDelayMs: number;
}

export const featureFlagDefaults: FeatureFlags = {
  enableSplitPayAuthEnhancement: false,
  disableColoradoAPR: false,
  enableWebOTPAutofill: false,
  isExchangedStreamlined: false,
  enableAddressLogging: false,
  useSmartyStreetsAPI: false,
  useVGSPaymentProvider: true,
  onCheckoutDelayMs: 500,
};

const FeatureFlagsContext = createContext<FeatureFlags>(featureFlagDefaults);

interface FeatureFlagsProviderProps {
  removeLoader: (loader: Loader) => void;
}

export const FeatureFlagsProvider: FCWithChildren<FeatureFlagsProviderProps> =
  ({ removeLoader, children }) => {
    const [featureFlags, setFeatureFlags] =
      useState<FeatureFlags>(featureFlagDefaults);
    const deviceID = useDeviceID();
    const { merchantID, programID } = useExperienceKeys();
    const { apiUrl, tenantID, featureFlagsEnabled } = useAppConfig();

    useEffect(() => {
      const fetchFeatureFlags = async () => {
        let flagResults: FeatureFlags = featureFlagDefaults;
        let response: Record<string, FlagResponse> | null = null;

        try {
          response = await getFeatureFlags(
            apiUrl,
            merchantID,
            tenantID,
            deviceID,
            programID
          );

          const enableSplitPayAuthEnhancement =
            response['splitpay_payment_auth_enhancement']?.result === 'true';
          const disableColoradoAPR = response['ColoradoAPR']?.result === 'true';
          const useSmartyStreetsAPI =
            response['use-smarty-streets-api']?.result === 'true';
          const enableWebOTPAutofill =
            response['webotp_autofill']?.result === 'true';
          const isExchangedStreamlined =
            response['exchanged-streamlined']?.result === 'true';
          const enableAddressLogging =
            response['checkout-address-logging']?.result === 'true';
          const useVGSPaymentProvider =
            response['use_vgs_payment_provider']?.result === 'true';
          const onCheckoutDelayMsFlag =
            response['on_checkout_delay_ms']?.result ||
            featureFlagDefaults.onCheckoutDelayMs.toString();
          const onCheckoutDelayMs = parseInt(onCheckoutDelayMsFlag, 10);

          flagResults = {
            enableSplitPayAuthEnhancement,
            disableColoradoAPR,
            enableWebOTPAutofill,
            isExchangedStreamlined,
            enableAddressLogging,
            useSmartyStreetsAPI,
            useVGSPaymentProvider,
            onCheckoutDelayMs,
          };

          setFeatureFlags(flagResults);
        } catch (err) {
          logger.error({ error: err }, 'Error accessing feature flags!');
        } finally {
          setAdditionalContext({
            feature_flags: JSON.stringify(flagResults).replace(/{|}|"/g, ''),
          });
          removeLoader('FEATURE_FLAGS');
        }
      };

      if (featureFlagsEnabled) {
        fetchFeatureFlags();
      }
    }, []);

    return (
      <FeatureFlagsContext.Provider value={featureFlags}>
        {children}
      </FeatureFlagsContext.Provider>
    );
  };

export const useFeatureFlags = (): FeatureFlags => {
  return useContext(FeatureFlagsContext);
};
